import {
  ICON__check,
  ICON__chevron_right,
  ICON__circle_exclamation_solid,
  ICON__eye_slash_solid,
  ICON__hammer_solid,
  ICON__image_solid,
  ICON__lock,
  ICON__square_solid,
} from '@/assets/icons_fa';
import { ICON__loader } from '@/assets/icons_lucide';
import { UI_PRIMITIVE__label_marquee } from '@/components/ui/UI_PRIMITIVE__label_marquee';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_slice_generation, use_slice_grid2d, use_slice_user } from '@/hooks/store/use_slices';
import { LOCAL__ui_set_string } from '@/state/ui/_base/LOCAL__ui_set_string';
import { set_current_generation } from '@/utils/utils_client_workspace_child_entity_viewers';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { ENUM__generation_status } from '@shared/dto/generation/_enums/ENUM__generation_status';
import { DTO__generation } from '@shared/dto/generation/DTO__generation';
import { DTO_TREE__generations_sliding_window } from '@shared/dto/generation/tree/DTO_TREE__generations_sliding_window';
import { DTO__grid2d_generation } from '@shared/dto/grid2d/DTO__grid2d_generation';
import { useEffect, useMemo, useRef, useState } from 'react';
import { G_HEADER__generations_history_item__buttons_bar } from './_buttons_item/G_HEADER__generations_history_item__buttons_bar';

interface PROPS {
  generation: DTO__generation;
  sliding_window: DTO_TREE__generations_sliding_window;
  is_bar_hovered: boolean;
}

export function G_HEADER__generations_history_item({ generation, sliding_window, is_bar_hovered }: PROPS) {
  const dispatch = use_dispatch();

  /*
   * STORE
   */
  const { generation_current, generations_map } = use_slice_generation(['generation_current', 'generations_map']);
  const { grids2d_map } = use_slice_grid2d(['grids2d_map']);
  const { user_settings } = use_slice_user(['user_settings']);

  /*
   * STATE
   */
  let { tool } = generation;
  let { graph } = tool;

  const ref_item = useRef<HTMLDivElement>(null);

  const has_children = useMemo(() => get_is_has_children(), [generation_current, generations_map]);

  const are_all_cell_generated = useMemo(() => get_are_all_cell_generated(), [generation, grids2d_map]);

  const am_i_relate_to_generation_current = useMemo(() => sliding_window.is_part_of_path(generation._id), [generation, sliding_window]);
  const am_i_generation_current = useMemo(() => generation_current._id === generation._id, [generation_current, generation]);

  const [is_hovered, set_is_hovered] = useState(false);

  const ref_has_scrolled_during_bar_hover = useRef(false);

  /*
   * EFFECTS
   */

  // Reset the hover-scroll memory whenever the bar is unhovered
  useEffect(() => {
    if (!is_bar_hovered) ref_has_scrolled_during_bar_hover.current = false;
  }, [is_bar_hovered]);

  useEffect(() => {
    if (!ref_item.current) return;

    if (!am_i_relate_to_generation_current) return;
    if (!am_i_generation_current) return;

    if (is_bar_hovered && ref_has_scrolled_during_bar_hover.current) return; //already scrolled during 1st hover

    /*
     *
     */
    let behavior: ScrollBehavior = 'smooth';

    if (!is_bar_hovered) behavior = 'instant';
    if (!ref_has_scrolled_during_bar_hover.current) behavior = 'instant';

    /*
     *
     */

    if (is_bar_hovered) ref_has_scrolled_during_bar_hover.current = true; //mark 1st hover

    ref_item.current.scrollIntoView({ block: 'nearest', behavior });
  }, [am_i_relate_to_generation_current, is_bar_hovered, am_i_generation_current]); // Triggers whenever the active item changes

  /*
   * EVENTS
   */

  async function onClick(e: React.MouseEvent) {
    if (am_i_generation_current) return;

    e.stopPropagation();

    await set_current_generation(generation._id);

    dispatch(LOCAL__ui_set_string({ key: 'g_tools__output_type', value: graph.meta.output_type, valid_values: [] }));
  }
  /*
   * METHODS
   */

  /*
   * METHODS
   */

  function get_is_has_children() {
    let out = Array.from(generations_map.values()).some(item => item.previous_generation_id == generation._id);

    return out;
  }

  function get_are_all_cell_generated() {
    if (!grids2d_map.has(generation._id)) return false;

    let grid2d = grids2d_map.get_or_assert(generation._id);
    assert_instanceof(grid2d, DTO__grid2d_generation);

    let out = grid2d.cells__get_list().length == grid2d.total_images_amount;

    return out;
  }

  /*
   * RENDERS
   */

  function render_icon_status() {
    //todo: make dynamic check is waiting for children
    //todo: is any child that has previous generation = this generation.id AND consume_field from this graph AND this generation.status == ADJUSTING
    //     case 'waiting for children':
    //       return <Hourglass className="w-3.5 h-3.5 text-indigo-400" />;
    //   }

    if (!generation.is_visible) return <ICON__eye_slash_solid className="size-3 bar-foreground" />;
    if (generation.is_locked()) return <ICON__lock className="size-3 bar-foreground" />;

    if (generation.is_in_progress) return <ICON__loader className="size-3  text-amber-400 animate-spin" />;
    if (are_all_cell_generated) return <ICON__check className="size-3 text-green-700" />;

    let out_map = new Map<ENUM__generation_status, JSX.Element>([
      [ENUM__generation_status.ADJUSTING, <ICON__hammer_solid className="size-3 text-cyan-400" />],
      [ENUM__generation_status.STOPPED, <ICON__square_solid className="size-3 text-red-500" />],
      [ENUM__generation_status.ERROR, <ICON__circle_exclamation_solid className="size-3 text-danger" />],
    ]);

    return out_map.get_or_assert(generation.status);
  }

  function render_icon_output_type() {
    return <ICON__image_solid className="size-3 text-blue-400" />;
    //   switch (type) {
    //     case 'image':
    //     case 'video':
    //       return <Video className="w-3.5 h-3.5 text-purple-400" />;
    //     case '3d model':
    //       return <Box className="w-3.5 h-3.5 text-emerald-400" />;
    //   }
  }

  function render_buttons_bar() {
    if (!is_hovered) return null;
    if (generation_current._id !== generation._id) return null;

    return <G_HEADER__generations_history_item__buttons_bar {...{ generation }} />;
  }

  function render_view() {
    return (
      <div
        ref={ref_item}
        {...{ onClick }}
        onMouseEnter={() => set_is_hovered(true)}
        onMouseLeave={() => set_is_hovered(false)}
        className={`relative 
        flex items-center justify-between p-1 
        bg-bar rounded 
        text-xs text-component-item-foreground
        cursor-pointer 
        transition-colors 
        cursor-pointer
        
        ${am_i_relate_to_generation_current ? '  ' : ' '}

        border
        ${am_i_relate_to_generation_current && !am_i_generation_current ? ' underline' : ''}
        ${am_i_generation_current ? 'border-component-item-border bg-component' : 'border-transparent'}
        ${am_i_generation_current || am_i_relate_to_generation_current ? 'opacity-100 ' : 'opacity-50'}
        

        hover:opacity-100
        hover:bg-component  hover:border-component-item-border
        `}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <span className={generation.graph_field_consumer.is_stub() ? 'opacity-0' : 'opacity-100'}>←</span>

          <div className="flex items-center gap-1 shrink-0">
            {render_icon_output_type()}
            {render_icon_status()}
          </div>

          <UI_PRIMITIVE__label_marquee word={tool.toString()} className="  text-xs" />
        </div>

        {has_children && !am_i_relate_to_generation_current && <ICON__chevron_right className="size-3 text-slate-500 shrink-0 ml-1" />}

        {render_buttons_bar()}
      </div>
    );
  }

  /*
   * HTML
   */

  if (am_i_generation_current) return render_view();
  if (am_i_relate_to_generation_current) return render_view();
  if (!generation.is_visible && user_settings.generation_show_hidden_generations) return null;

  return render_view();
}
